// "Group by" for the timeline: any combination of Team, Pod and Workstream,
// always nested in that order. The first chosen level becomes the lanes, the
// next the captioned bands inside each lane, the third lighter blocks inside
// those. Nothing chosen = one flat lane.
//
// Defaults to all three. Once someone changes it, their choice is remembered
// in this browser (localStorage); a ?group= link still wins, so shared links
// reproduce the sender's view.

import type { Initiative } from "./types";
import { TEAMS } from "./constants";

export type GroupLevel = "team" | "pod" | "workstream";
export type GroupBy = Record<GroupLevel, boolean>;

export const GROUP_LEVELS: readonly GroupLevel[] = ["team", "pod", "workstream"];
export const GROUP_LEVEL_LABELS: Record<GroupLevel, string> = { team: "Team", pod: "Pod", workstream: "Workstream" };
export const DEFAULT_GROUP_BY: GroupBy = { team: true, pod: true, workstream: true };

const PREF_KEY = "roadmap.groupBy";

export function activeLevels(g: GroupBy): GroupLevel[] {
  return GROUP_LEVELS.filter(l => g[l]);
}

// URL form: "team,pod,workstream", or "none" for flat.
export function formatGroupParam(g: GroupBy): string {
  const levels = activeLevels(g);
  return levels.length ? levels.join(",") : "none";
}

// Unrecognized values are ignored. A pre-pods "?group=team" link means team
// lanes only, which is exactly what it showed when it was shared.
export function parseGroupParam(raw: string | null): GroupBy | null {
  if (raw === null) return null;
  if (raw === "none") return { team: false, pod: false, workstream: false };
  const parts = raw.split(",").map(s => s.trim());
  if (!parts.some(p => (GROUP_LEVELS as readonly string[]).includes(p))) return null;
  return { team: parts.includes("team"), pod: parts.includes("pod"), workstream: parts.includes("workstream") };
}

export function loadGroupByPref(): GroupBy | null {
  try {
    return parseGroupParam(window.localStorage.getItem(PREF_KEY));
  } catch {
    return null;
  }
}

export function saveGroupByPref(g: GroupBy): void {
  try {
    window.localStorage.setItem(PREF_KEY, formatGroupParam(g));
  } catch {
    // Blocked storage just means the default next time.
  }
}

// ---------- workstream names ----------

// Distinct workstream names in use, case-insensitively de-duplicated
// (first spelling wins), sorted — for pickers and the filter chip.
export function workstreamNames(initiatives: Initiative[]): string[] {
  const seen = new Map<string, string>();
  for (const i of initiatives) {
    const w = i.workstream?.trim();
    if (w && !seen.has(w.toLowerCase())) seen.set(w.toLowerCase(), w);
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export const NO_WORKSTREAM = "__none";

// ---------- the group tree ----------

export type GroupNode = {
  id: string;
  name: string;          // "" = uncaptioned (e.g. no workstream, nested)
  eyebrow?: string;      // lane-level context, e.g. the team above a pod lane
  level?: GroupLevel;    // which grouping produced this node (unset for the flat lane)
  initiatives: Initiative[];
  children?: GroupNode[];
};

function byTeam(items: Initiative[]): GroupNode[] {
  // Every team gets a lane, even an empty one — the team list is the shape
  // of the org. Unknown teams (renamed/removed) collect at the end.
  const nodes: GroupNode[] = TEAMS.map(t => ({ id: t.id, name: t.name, initiatives: items.filter(i => i.teamId === t.id) }));
  const rest = items.filter(i => !TEAMS.some(t => t.id === i.teamId));
  if (rest.length) nodes.push({ id: "_other-team", name: "Other", initiatives: rest });
  return nodes;
}

function byPod(items: Initiative[], teamId: string | undefined): GroupNode[] {
  if (teamId !== undefined) {
    const team = TEAMS.find(t => t.id === teamId);
    const pods = team?.pods ?? [];
    const nodes: GroupNode[] = pods
      .map(p => ({ id: p.id, name: p.name, initiatives: items.filter(i => i.podId === p.id) }))
      .filter(n => n.initiatives.length > 0);
    const rest = items.filter(i => !pods.some(p => p.id === i.podId));
    // A team without pods (UX Research) needs no "No pod" caption.
    if (rest.length) nodes.push({ id: "_no-pod", name: pods.length ? "No pod" : "", initiatives: rest });
    return nodes;
  }
  // Pods as the top level: one lane per pod, its team as the eyebrow.
  const nodes: GroupNode[] = [];
  for (const t of TEAMS) {
    for (const p of t.pods) {
      const its = items.filter(i => i.teamId === t.id && i.podId === p.id);
      if (its.length) nodes.push({ id: `${t.id}:${p.id}`, name: p.name, eyebrow: t.name, initiatives: its });
    }
    const loose = items.filter(i => i.teamId === t.id && !t.pods.some(p => p.id === i.podId));
    if (loose.length) nodes.push({ id: `${t.id}:_no-pod`, name: t.pods.length ? "No pod" : t.name, eyebrow: t.pods.length ? t.name : undefined, initiatives: loose });
  }
  const rest = items.filter(i => !TEAMS.some(t => t.id === i.teamId));
  if (rest.length) nodes.push({ id: "_other-team", name: "Other", initiatives: rest });
  return nodes;
}

function byWorkstream(items: Initiative[], topLevel: boolean): GroupNode[] {
  const nodes: GroupNode[] = workstreamNames(items).map(name => ({
    id: `ws:${name.toLowerCase()}`,
    name,
    initiatives: items.filter(i => i.workstream?.trim().toLowerCase() === name.toLowerCase()),
  }));
  const none = items.filter(i => !i.workstream?.trim());
  // Nested, unassigned work sits uncaptioned at the bottom; as lanes it
  // needs a name.
  if (none.length) nodes.push({ id: NO_WORKSTREAM, name: topLevel ? "No workstream" : "", initiatives: none });
  return nodes;
}

// The lanes for a set of initiatives under the chosen levels.
export function buildGroupTree(items: Initiative[], levels: GroupLevel[]): GroupNode[] {
  if (levels.length === 0) return [{ id: "all", name: "All initiatives", initiatives: items }];
  const split = (its: Initiative[], depth: number, teamId: string | undefined): GroupNode[] => {
    const level = levels[depth];
    const nodes = level === "team" ? byTeam(its)
      : level === "pod" ? byPod(its, teamId)
      : byWorkstream(its, depth === 0);
    for (const n of nodes) n.level = level;
    if (depth + 1 < levels.length) {
      for (const n of nodes) {
        n.children = split(n.initiatives, depth + 1, level === "team" ? n.id : teamId);
      }
    }
    return nodes;
  };
  return split(items, 0, undefined);
}
